import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ProfilePreviewDialog from "crqlar-react-common/dist/components/profilePreview/ProfilePreviewDialog";
import SurveyInviteButton from "../surveys/share/SurveyInviteButton";
import ProfileExportConfirmDialog from "crqlar-react-common/dist/components/profile/ProfileExportConfirmDialog";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import type {ProfileListItem} from "crqlar-react-common/dist/models/profile/profileListItem.model";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {
    type AudienceMembersRequest,
    AudienceService
} from "crqlar-react-common/dist/services/audience/audience.service";
import {MixpanelService} from "crqlar-react-common/dist/services/analytics/mixpanel.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useSnackbar} from "notistack";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {useSearchParams} from "react-router-dom";
import {PageShell} from "../layout/PageShell";
import {
    RightSidebarActionButton,
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailLayout
} from "../layout/rightSidebar/RightSidebarRail";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailFullscreen,
    ServerTableRailPrint,
    ServerTableRailSort,
    useServerTable
} from "../common/serverTable";
import {ElementChip} from "../common/uiElements/base/ElementChip";
import {useProfileListColumns} from "../crm/profileListColumns";
import {
    parseAudienceMemberUrlState,
    writeAudienceMemberUrlState
} from "./audienceMemberUrlState";

/** Print pages through the members endpoint up to this many rows, as the old print document did. */
const PRINT_LIMIT = 5000;

const AudienceMemberList = () => {
    useTrackScreenVisit('audience member list');

    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();
    const readOnly = useReadOnly();
    const [searchParams, setSearchParams] = useSearchParams();
    const [openExportConfirmDialog, setOpenExportConfirmDialog] = useState(false);
    const [selectedProfileId, setSelectedProfileId] = useState<string>();
    const searchParamsValue = searchParams.toString();
    const audienceUrlState = useMemo(
        () => parseAudienceMemberUrlState(new URLSearchParams(searchParamsValue)),
        [searchParamsValue]
    );
    const {audienceId, filter, invalidFilter, mode} = audienceUrlState;
    const marketingSize = mode === 'marketing';
    const missingAudienceScope = !audienceId && filter === undefined;
    const urlStateIsInvalid = invalidFilter || missingAudienceScope;
    const filterIdentity = JSON.stringify(filter);

    useEffect(() => {
        if (!audienceUrlState.needsMigration) return;

        const nextSearchParams = new URLSearchParams(searchParamsValue);
        writeAudienceMemberUrlState(nextSearchParams, audienceUrlState);
        setSearchParams(nextSearchParams, {replace: true, preventScrollReset: true});
    }, [audienceUrlState, searchParamsValue, setSearchParams]);

    const audienceQuery = useQuery(
        ['audienceMembersAudience', audienceId],
        () => AudienceService.getAudienceById(audienceId!),
        {enabled: !!audienceId}
    );

    const columns = useProfileListColumns();

    const createMembersRequest = useCallback((
        offset: number,
        limit: number,
        sorting?: {field: string; direction: 'ASC' | 'DESC'}
    ): AudienceMembersRequest => ({
        ...(audienceId ? {id: audienceId} : {filter}),
        marketingSize,
        limit: {
            offset,
            limit,
            sort: sorting ? [sorting] : undefined
        }
    }), [audienceId, filter, marketingSize]);

    const controller = useServerTable<ProfileListItem>({
        tableId: 'marketing-audience-members',
        columns,
        getRowId: profile => profile.id,
        onRowClick: profile => setSelectedProfileId(profile.id),
        initialSorting: [{id: 'id', desc: true}],
        allRows: {maxRows: PRINT_LIMIT},
        enabled: !urlStateIsInvalid,
        queryDependencies: [audienceId, filterIdentity, marketingSize, urlStateIsInvalid],
        queryFn: async ({pagination, sorting}) => {
            const page = await AudienceService.getMembers(createMembersRequest(
                pagination.pageIndex * pagination.pageSize,
                pagination.pageSize,
                sorting
            ));

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    const total = controller.query.data?.totalElements ?? 0;
    const headerName = audienceId
        ? audienceQuery.data?.name || t('Audience')
        : t('Filter preview');

    const exportMutation = useMutation(
        ['exportAudienceMembers', audienceId, filterIdentity, marketingSize],
        (selectedExportFields: string[]) => AudienceService.exportMembers({
            ...createMembersRequest(0, 0, {field: 'id', direction: 'DESC'}),
            selectedExportFields
        }),
        {
            onSuccess: () => {
                enqueueSnackbar(t('Profiles exported successfully'), {variant: 'success'});
                MixpanelService.getInstance().track('export audience members');
            },
            onError: () => {
                enqueueSnackbar(t('Could not export profiles'), {variant: 'error'});
            }
        }
    );

    // Rows are lightweight list items; the full profile loads only when a row is opened.
    const selectedProfileQuery = useQuery(
        ['audience-member-profile', selectedProfileId],
        () => ProfileService.getProfileById(selectedProfileId!, true),
        {
            enabled: Boolean(selectedProfileId),
            onError: () => {
                enqueueSnackbar(t('Could not load profile'), {variant: 'error'});
                setSelectedProfileId(undefined);
            }
        }
    );

    const handleProfileUpdate = (profile: Profile) => {
        queryClient.setQueryData(['audience-member-profile', selectedProfileId], profile);
        void controller.query.refetch();
    };

    return (
        <PageShell
            icon={<GroupsOutlinedIcon fontSize="inherit"/>}
            title={headerName}
            documentTitle={t('Audience members')}
            tabs={[]}
        >
            <>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <RightSidebarRailLayout>
                            <RightSidebarRailHeader>
                                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                    <ElementChip text="Audience Preview"/>
                                    <ElementChip text={`${total} total profiles`}/>
                                    {exportMutation.isLoading && (
                                        <Typography variant="body2" color="text.secondary">
                                            {t('Preparing export')}…
                                        </Typography>
                                    )}
                                </Stack>
                            </RightSidebarRailHeader>

                            {urlStateIsInvalid && (
                                <Alert severity="error" sx={{mx: 2, mb: 1}}>
                                    {t('This audience preview link is missing or contains an invalid filter.')}
                                </Alert>
                            )}
                            <ServerTable/>

                            {selectedProfileQuery.isLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255, 255, 255, 0.5)',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <CircularProgress size={28}/>
                                </Box>
                            )}

                            <RightSidebarRail>
                                <ServerTableRailSort/>
                                <ServerTableRailFullscreen/>
                                <ServerTableRailColumns/>
                                <RightSidebarDivider/>
                                <RightSidebarActionButton
                                    icon={exportMutation.isLoading
                                        ? <CircularProgress size={20}/>
                                        : <DownloadOutlinedIcon/>}
                                    label={t('Export')}
                                    disabled={readOnly || urlStateIsInvalid || exportMutation.isLoading}
                                    onClick={() => setOpenExportConfirmDialog(true)}
                                />
                                <ServerTableRailPrint title={headerName} disabled={urlStateIsInvalid}/>
                            </RightSidebarRail>
                        </RightSidebarRailLayout>
                    </ServerTableFrame>
                </ServerTableProvider>

                <ProfilePreviewDialog
                    open={Boolean(selectedProfileQuery.data)}
                    onClose={() => setSelectedProfileId(undefined)}
                    profile={selectedProfileQuery.data}
                    setProfile={handleProfileUpdate}
                    extraActions={<SurveyInviteButton profileId={selectedProfileQuery.data?.id}/>}
                />

                <ProfileExportConfirmDialog
                    open={openExportConfirmDialog}
                    onClose={() => setOpenExportConfirmDialog(false)}
                    onConfirm={selectedFields => {
                        setOpenExportConfirmDialog(false);
                        exportMutation.mutate(selectedFields);
                    }}
                    profilesCount={total}
                />
            </>
        </PageShell>
    );
};

export default AudienceMemberList;
